/**
 * How an alert will be delivered, in one sentence (PRD-1447).
 *
 * Shown where the author creates an alert straight from the conversation, in
 * place of the Recipients and Schedule steps they no longer have to click
 * through. The point is that those choices are still *visible* — a default the
 * author never sees is a default they never chose — while not being a step.
 *
 * Read from the same form state the steps edit, so what this says and what is
 * saved cannot disagree.
 */

import { withPendingRecipient } from './alertRecipients';
import { formatScheduleTime } from '../../../shared/datetime/timestamps';
import { defaultScheduleZone } from '../shared/ScheduleZoneField';

const CHANNEL_WORDS = { email: 'email', sms: 'SMS' };

const DAYS = {
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
  saturday: 'Saturday',
  sunday: 'Sunday',
};

const joinWords = (words) => {
  if (words.length <= 1) return words[0] ?? '';
  return `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`;
};

const describeChannels = (channels) => {
  const named = (channels ?? []).map((c) => CHANNEL_WORDS[c] ?? c);
  return named.length ? `by ${joinWords(named)}` : '';
};

// A typed address not yet added counts, because the save folds it in too: a
// summary saying "to you" while a colleague's address is about to be sent is
// the disagreement this module exists to prevent (review of #212).
const describeRecipients = (raw) => {
  const formData = withPendingRecipient(raw);
  const toSelf = formData?.reportEmailToSelf !== false;
  const others = (formData?.reportRecipients ?? []).filter((e) => String(e || '').trim() !== '');
  const parts = [];
  if (toSelf) parts.push('you');
  if (others.length === 1) parts.push('1 other person');
  else if (others.length > 1) parts.push(`${others.length} other people`);
  return parts.length ? `to ${joinWords(parts)}` : '';
};

// The same defaults `buildCreatePayload` applies, so an unset field is described
// as what will actually be saved.
const describeSchedule = (trigger, zone) => {
  const time = formatScheduleTime(trigger?.time || '08:00', zone);
  const frequency = trigger?.frequency || 'daily';
  if (frequency === 'daily') return `daily at ${time}`;
  if (frequency === 'weekly') return `every ${DAYS[trigger?.dayOfWeek] ?? 'Monday'} at ${time}`;
  // `ReportStepSchedule`: "run on the 1st of each month at the time selected".
  if (frequency === 'monthly') return `on the 1st of each month at ${time}`;
  // Said rather than guessed. Describing an unknown frequency as "daily" would
  // be a summary that disagrees with what is saved, which is the one thing this
  // summary must not do.
  return `on its ${frequency} schedule at ${time}`;
};

/** "Sent by email to you, daily at 8:00 AM ADT." */
export const describeDelivery = (formData) => {
  const how = [describeChannels(formData?.alert?.channels), describeRecipients(formData)]
    .filter(Boolean)
    .join(' ');
  const when = describeSchedule(formData?.trigger, formData?.timezone || defaultScheduleZone());
  return how ? `Sent ${how}, ${when}.` : `Sent ${when}.`;
};
